import { StandardJourneyFieldError } from '~/components/StandardJourney';
import JourneyAddressFields from '~/components/StandardJourney/JourneyAddressFields';
import { useJourneyAddressForm } from '~/components/StandardJourney/useJourneyAddressForm';
import { lookupRenewalZipcode } from '~/services/modules/renewal';
import { formatRenewalAddressLine, formatRenewalZipcode, type RenewalAddressFields } from '../renewalReview';
import { RenewalReviewCard, RenewalReviewEditActions, RenewalReviewField } from './RenewalReviewCard';
import { useRenewalCardSave } from './useRenewalCardSave';

type Props = {
  appId: string;
  data: RenewalAddressFields;
  editing: boolean;
  editDisabled: boolean;
  onEdit: () => void;
  onClose: () => void;
  onSave: (values: RenewalAddressFields) => Promise<void>;
};

/** Card «Endereço completo» (onde o equipamento fica guardado), com autopreenchimento pelo CEP. */
export default function RenewalAddressCard({ appId, data, editing, editDisabled, onEdit, onClose, onSave }: Props) {
  const address = useJourneyAddressForm(data, lookupRenewalZipcode);
  const { saving, serverError, clearServerError, run } = useRenewalCardSave(
    'Não foi possível salvar o endereço. Revise as informações e tente novamente.'
  );

  const reset = () => {
    address.reset(data);
    clearServerError();
  };

  const startEdit = () => {
    reset();
    onEdit();
  };

  const cancel = () => {
    reset();
    onClose();
  };

  const save = async () => {
    if (!address.validate()) return;
    if (await run(() => onSave(address.form))) onClose();
  };

  return (
    <RenewalReviewCard
      appId={appId}
      editDisabled={editDisabled}
      editLabel="Editar endereço"
      editing={editing}
      onEdit={startEdit}
      subtitle="(Onde o equipamento fica guardado)"
      title="Endereço completo"
    >
      {editing ? (
        <>
          <JourneyAddressFields
            appId={appId}
            errors={address.errors}
            form={address.form}
            onChange={address.change}
            onZipcodeChange={address.changeZipcode}
          />
          {serverError ? (
            <StandardJourneyFieldError
              appId={`${appId}-server-error`}
              id={`${appId}-server-error`}
              message={serverError}
            />
          ) : null}
          <RenewalReviewEditActions appId={appId} onCancel={cancel} onSave={save} saving={saving} />
        </>
      ) : (
        <>
          <RenewalReviewField label="Endereço" value={formatRenewalAddressLine(data)} />
          <RenewalReviewField label="CEP" value={formatRenewalZipcode(data.zipcode)} />
        </>
      )}
    </RenewalReviewCard>
  );
}
